import {AppApi} from 'store/customer/api';

export interface DistanceBand {
    from_km: number;
    to_km: number | null;
    price: number;
}

export interface PackagingOption {
    id: number;
    group_id: number;
    name: string;
    description: string;
    costing_method: number;
    amount: number | null;
    price_type: number;
    distance_bands: DistanceBand[];
    depot_ids: number[];
    available_for: number;
    multiselectable: boolean;
    is_active?: boolean;
    is_visible: boolean;
    all_depots: boolean;
    sort_order: number;
    customer_amount?: number | null;
    preselected?: boolean;
    calculated_amount?: number | null;
    distance_km?: number | null;
    distance_pending?: boolean;
    price_confirmed_after_review?: boolean;
}

export interface DeliveryExtraGroup {
    id: number;
    name: string;
    customer_heading: string;
    category: number;
    dispatch_methods: number;
    selection_mode: number;
    is_visible: boolean;
    sort_order: number;
    options: PackagingOption[];
}

export interface CustomerDeliveryExtraGroups {
    groups: DeliveryExtraGroup[];
    defaultFreightCost: number;
    defaultFreightApplies: boolean;
}

export interface JobPackagingOption extends PackagingOption {
    id: number;
    job_id: number;
    packaging_option_id: number | null;
    group_name: string;
    category: number;
    selection_mode: number;
    confirmed_amount: number | null;
    submitted_amount: number | null;
    distance_km: number | null;
    is_confirmed: boolean;
}

export interface JobPackagingOptions {
    options: JobPackagingOption[];
}

const packagingOptionApi = AppApi.injectEndpoints({
    endpoints: (build) => ({
        getCustomerDeliveryExtraGroups: build.query<
            CustomerDeliveryExtraGroups,
            {
                depotId?: number;
                dispatchMethod: number;
                deliveryAddress?: string;
                resolveDistance: boolean;
            }
        >({
            query: ({
                depotId,
                dispatchMethod,
                deliveryAddress,
                resolveDistance,
            }) => ({
                url: 'delivery-extra-groups',
                params: {
                    ...(depotId ? {depot_id: depotId} : {}),
                    dispatch_method: dispatchMethod,
                    ...(deliveryAddress
                        ? {delivery_address: deliveryAddress}
                        : {}),
                    resolve_distance: resolveDistance,
                },
            }),
            transformResponse: (response: {
                data: {
                    groups: DeliveryExtraGroup[];
                    default_freight_cost: number;
                    default_freight_applies: boolean;
                };
            }) => ({
                groups: response.data.groups,
                defaultFreightCost: Number(
                    response.data.default_freight_cost || 0
                ),
                defaultFreightApplies: Boolean(
                    response.data.default_freight_applies
                ),
            }),
            providesTags: ['PackagingOption'],
        }),
        getJobPackagingOptions: build.query<
            JobPackagingOptions,
            {jobId: number}
        >({
            query: ({jobId}) => `job/${jobId}/packaging-options`,
            transformResponse: (response: {
                data: {
                    options: JobPackagingOption[];
                };
            }) => ({
                options: response.data.options.map((option) => ({
                    ...option,
                    id: Number(option.id),
                    packaging_option_id:
                        option.packaging_option_id == null
                            ? null
                            : Number(option.packaging_option_id),
                })),
            }),
            providesTags: (result, error, {jobId}) => [
                {type: 'JobPackagingOptions', id: jobId},
            ],
        }),
        reconcileJobPackagingOptions: build.mutation<void, {jobId: number}>({
            query: ({jobId}) => ({
                url: `job/${jobId}/packaging-options/reconcile`,
                method: 'POST',
            }),
            invalidatesTags: (result, error, {jobId}) => [
                {type: 'JobPackagingOptions', id: jobId},
                {type: 'JobCost', jobId},
            ],
        }),
    }),
});

export const {
    useGetCustomerDeliveryExtraGroupsQuery,
    useGetJobPackagingOptionsQuery,
    useReconcileJobPackagingOptionsMutation,
} = packagingOptionApi;
